import React, { useState, useCallback } from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import ItemConfigForm from './ItemConfigForm';
import modalStyles from '../../../../styles/modals.module.css';
import styles from './ItemConfigWizard.module.css';

/**
 * ItemOptionsEditor - Mini-modal for editing an existing project item's options
 *
 * Props:
 * - projectItem: The project_item being edited (includes master_items join)
 * - masterItem: The full master item data (for options, estimated_cost, etc.)
 * - onSave: Callback when save is clicked - receives updated config
 * - onClose: Callback when modal is closed
 * - costTypes: Array of cost types for dropdown
 */
function ItemOptionsEditor({ projectItem, masterItem, onSave, onClose, costTypes = [] }) {
  // Initialize config from existing project item data
  const [config, setConfig] = useState(() => ({
    quantity: projectItem?.quantity ?? 1,
    cost_override: projectItem?.cost_override ?? null,
    incl_vat: projectItem?.incl_vat ?? true,
    selected_options: projectItem?.selected_option ?? {},
    selected_options_data: {}, // Will be populated on change
    cost_type_id: projectItem?.cost_type_id ?? masterItem?.cost_type_id ?? null,
  }));

  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle field changes from ItemConfigForm
  const handleChange = useCallback((field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await onSave({
        id: projectItem.id,
        quantity: config.quantity,
        cost_override: config.cost_override,
        incl_vat: config.incl_vat,
        selected_option: config.selected_options,
        cost_type_id: config.cost_type_id,
      });
      onClose();
    } catch (err) {
      setError('אירעה שגיאה בשמירת השינויים');
    } finally {
      setIsSubmitting(false);
    }
  }, [projectItem?.id, config, onSave, onClose]);

  // Handle overlay click to close
  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!projectItem || !masterItem) return null;

  const itemName = masterItem.item_name || projectItem.master_items?.item_name || 'פריט';

  return (
    <div className={modalStyles.overlay} onClick={handleOverlayClick}>
      <div className={modalStyles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={modalStyles.header}>
          <h2 className={styles.itemName}>{itemName}</h2>
          <div className={styles.stepTitle}>עריכת הגדרות</div>
        </div>

        {/* Content */}
        <div className={modalStyles.content}>
          {error && <div className={modalStyles.error}>{error}</div>}

          <ItemConfigForm
            item={masterItem}
            config={config}
            onChange={handleChange}
            costTypes={costTypes}
            showVat={true}
            showQuantity={true}
          />
        </div>

        {/* Footer */}
        <div className={modalStyles.footer}>
          <button className={modalStyles.cancelButton} onClick={onClose}>
            ביטול
          </button>
          <button
            className={modalStyles.primaryButton}
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'שומר...' : 'שמור'}
            {!isSubmitting && <ArrowLeft size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(ItemOptionsEditor);
